import React from 'react';

import {
  Form,
  Input,
  Button,
  Checkbox,
  Typography,
  message,
} from 'antd';

import {
  MailOutlined,
  LockOutlined,
  ArrowRightOutlined,
  SafetyOutlined,
  BarChartOutlined,
  UserAddOutlined,
} from '@ant-design/icons';

import {
  useNavigate,
} from 'react-router-dom';

import {
  Lotto649Logo,
  LottoMaxLogo,
  RouletteLogo,
} from '../../components/ProductLogo';

import {
  useAuth,
} from '../../contexts/AuthContext';

import './index.scss';


const { Text } = Typography;


/* =========================================================
   Product Brand Bar
========================================================= */

function ProductBrandBar() {

  return (

    <div className="product-brand-bar">

      {/* Lotto 6/49 */}

      <div className="product-logo">

        <Lotto649Logo
          height={38}
        />

      </div>


      <div className="brand-divider" />


      {/* Lotto MAX */}

      <div className="product-logo lotto-max-logo">

        <LottoMaxLogo
          height={38}
        />

      </div>


      <div className="brand-divider" />


      {/* Roulette */}

      <div className="product-logo">

        <RouletteLogo
          height={38}
        />

      </div>

    </div>

  );

}


/* =========================================================
   Login
========================================================= */

export default function Login() {

  const navigate =
    useNavigate();


  const {
    login,
    loading,
  } = useAuth();


  /* =======================================================
     Submit Login
  ======================================================= */

  const handleFinish = async (
    values
  ) => {

    console.log(
      'Login:',
      values
    );


    try {

      const result =
        await login(
          values.email,
          values.password
        );


      /* ---------------------------------------------------
         Login Success
      --------------------------------------------------- */

      if (result.success) {

        console.log(
          'Login successful:',
          result.user
        );


        message.success(
          'Login successful'
        );


        /*
         * AuthContext has already updated
         * the authenticated user state.
         */

        navigate(
          '/',
          {
            replace: true,
          }
        );


        return;

      }


      /* ---------------------------------------------------
         Login Failed
      --------------------------------------------------- */

      message.error(
        result.message ||
        'Invalid email or password'
      );

    } catch (error) {

      console.error(
        'Login error:',
        error
      );


      message.error(
        'Unable to sign in. Please try again.'
      );

    }

  };


  /* =======================================================
     Register
  ======================================================= */

  const handleRegister = () => {

    navigate(
      '/register'
    );

  };


  /* =======================================================
     Forgot Password
  ======================================================= */

  const handleForgotPassword = () => {

    /*
     * We will implement:
     *
     * /forgot-password
     *
     * in the password recovery stage.
     */

    message.info(
      'Password recovery will be available soon.'
    );

  };


  /* =======================================================
     Render
  ======================================================= */

  return (

    <div className="login-page">


      {/* ===================================================
          Background
      =================================================== */}

      <div className="background-grid" />

      <div
        className="
          background-glow
          glow-one
        "
      />

      <div
        className="
          background-glow
          glow-two
        "
      />


      <div className="login-container">


        {/* =================================================
            Product Logos
        ================================================= */}

        <ProductBrandBar />


        {/* =================================================
            Header
        ================================================= */}

        <div className="login-header">

          <div className="platform-icon">

            <BarChartOutlined />

          </div>


          <h1>
            Lotto Analytics
          </h1>


          <p>
            Advanced Lottery & Gaming Analytics
          </p>

        </div>


        {/* =================================================
            Login Card
        ================================================= */}

        <div className="login-card">


          {/* =================================================
              Card Header
          ================================================= */}

          <div className="card-header">

            <h2>
              Welcome Back
            </h2>


            <p>
              Sign in to continue to your account
            </p>

          </div>


          {/* =================================================
              Login Form
          ================================================= */}

          <Form

            layout="vertical"

            requiredMark={false}

            onFinish={
              handleFinish
            }

            autoComplete="off"

          >


            {/* =================================================
                Email
            ================================================= */}

            <Form.Item

              label="Email Address"

              name="email"

              rules={[

                {
                  required: true,

                  message:
                    'Please enter your email address',
                },

                {
                  type: 'email',

                  message:
                    'Please enter a valid email address',
                },

              ]}

            >

              <Input

                size="large"

                prefix={
                  <MailOutlined />
                }

                placeholder="
                  Enter your email
                "

                disabled={
                  loading
                }

              />

            </Form.Item>


            {/* =================================================
                Password
            ================================================= */}

            <Form.Item

              label="Password"

              name="password"

              rules={[

                {
                  required: true,

                  message:
                    'Please enter your password',
                },

              ]}

            >

              <Input.Password

                size="large"

                prefix={
                  <LockOutlined />
                }

                placeholder="
                  Enter your password
                "

                disabled={
                  loading
                }

              />

            </Form.Item>


            {/* =================================================
                Remember / Forgot
            ================================================= */}

            <div className="login-options">

              <Checkbox
                disabled={
                  loading
                }
              >

                Remember me

              </Checkbox>


              <button

                type="button"

                className="
                  forgot-password
                "

                disabled={
                  loading
                }

                onClick={
                  handleForgotPassword
                }

              >

                Forgot password?

              </button>

            </div>


            {/* =================================================
                Submit
            ================================================= */}

            <Form.Item
              className="
                submit-item
              "
            >

              <Button

                type="primary"

                htmlType="submit"

                size="large"

                block

                className="
                  login-button
                "

                icon={
                  <ArrowRightOutlined />
                }

                loading={
                  loading
                }

                disabled={
                  loading
                }

              >

                {
                  loading
                    ? 'Signing In...'
                    : 'Sign In'
                }

              </Button>

            </Form.Item>

          </Form>


          {/* =================================================
              Register Divider
          ================================================= */}

          <div className="account-divider">

            <span>
              New to Lotto Analytics?
            </span>

          </div>


          {/* =================================================
              Create Account
          ================================================= */}

          <Button

            size="large"

            block

            className="
              register-button
            "

            icon={
              <UserAddOutlined />
            }

            onClick={
              handleRegister
            }

            disabled={
              loading
            }

          >

            Create Account

          </Button>


          {/* =================================================
              Security
          ================================================= */}

          <div className="security-message">

            <SafetyOutlined />

            <span>
              Secure account access
            </span>

          </div>

        </div>


        {/* =================================================
            Membership Preview
        ================================================= */}

        <div className="membership-preview">

          <div className="membership-title">

            Premium Analytics

          </div>


          <div className="membership-products">

            <span>
              Lotto 6/49
            </span>

            <span>
              •
            </span>

            <span>
              Lotto MAX
            </span>

            <span>
              •
            </span>

            <span>
              Roulette
            </span>

          </div>

        </div>


        {/* =================================================
            Footer
        ================================================= */}

        <div className="login-footer">

          <Text>

            © {new Date().getFullYear()}
            {' '}
            Lotto Analytics

          </Text>


          <div className="footer-links">

            <span>
              Privacy
            </span>

            <span>
              Terms
            </span>

          </div>

        </div>


      </div>

    </div>

  );

}